import { useRef, useState, type ReactNode } from 'react'
import { Lookup, type LookupItem, type LookupSection, type LookupType, type LookupFacet } from './Lookup'
import { Form, type FormField } from './Form'
import { Pill } from './Pill'
import { cx } from './cx'
import s from './RelationPanel.module.css'

/**
 * RelationPanel — the 1:N / N:N subgrid. Lists the related records/edges, with
 * Add (Lookup, multi → basket), New (quick-create, caller-owned), edit-the-EDGE
 * (a Form over the edge `attributes` — the Dynamics-beating bit), remove, and
 * temporal current-vs-past. Composes Lookup + Form; every edge carries its own data.
 */
export interface RelationEdge {
  id: string                 // the edge id
  item: LookupItem           // the related record, rendered
  summary?: ReactNode        // the edge-attribute summary line (e.g. "CFO · primary")
  attrs?: Record<string, unknown>
  ended?: boolean            // temporal — a past relationship
}
export interface RelationPanelProps {
  title: string
  hint?: ReactNode
  edges: RelationEdge[]
  addSearch?: (q: string, facet: string | null, type: string | null) => LookupSection[] | Promise<LookupSection[]>
  addTypes?: LookupType[]
  addFacets?: (q: string) => LookupFacet[]
  addCreate?: { show: (q: string) => boolean; label: (q: string) => ReactNode; onCreate: (q: string) => void | Promise<void> }
  onAdd?: (ids: string[]) => void
  onNew?: () => void
  onRemove?: (edgeId: string) => void
  edgeFields?: FormField[]
  onEditEdge?: (edgeId: string, attrs: Record<string, unknown>) => void
  emptyText?: ReactNode
  testid?: string
}

export function RelationPanel({
  title, hint, edges, addSearch, addTypes, addFacets, addCreate, onAdd, onNew, onRemove,
  edgeFields, onEditEdge, emptyText = 'Nothing linked yet.', testid,
}: RelationPanelProps) {
  const [adding, setAdding] = useState(false)
  const [showPast, setShowPast] = useState(false)
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState<Record<string, unknown>>({})
  const addRef = useRef<HTMLButtonElement>(null)

  const current = edges.filter((e) => !e.ended)
  const past = edges.filter((e) => e.ended)
  const shown = showPast ? edges : current

  const startEdit = (e: RelationEdge) => { setEditing(e.id); setDraft({ ...(e.attrs ?? {}) }) }
  const saveEdit = () => { if (editing) onEditEdge?.(editing, draft); setEditing(null) }

  return (
    <div className={s.panel} data-testid={testid}>
      <div className={s.head}>
        <span className={s.title}>{title}</span>
        {hint && <span className={s.hint}>{hint}</span>}
        <span className={s.grow} />
        {addSearch && onAdd && <button ref={addRef} type="button" className={s.act} onClick={() => setAdding(true)}>＋ Add</button>}
        {onNew && <button type="button" className={s.act} onClick={onNew}>＋ New</button>}
      </div>

      {shown.length === 0 && <div className={s.empty}>{emptyText}</div>}

      {shown.map((e) => (
        <div key={e.id}>
          <div className={cx(s.row, e.ended && s.ended)} data-testid={testid ? `${testid}-row` : undefined}>
            {e.item.glyph != null && <span className={s.glyph}>{e.item.glyph}</span>}
            <div className={s.main}>
              <div className={s.primary}>{e.item.primary}</div>
              {(e.summary ?? e.item.secondary) != null && <div className={s.summary}>{e.summary ?? e.item.secondary}</div>}
            </div>
            {e.item.badge && <Pill tone={e.item.badge.tone ?? 'neutral'}>{e.item.badge.label}</Pill>}
            {e.ended && <span className={s.pastTag}>ended</span>}
            <div className={s.rowActs}>
              {edgeFields && onEditEdge && !e.ended && (
                <button type="button" className={s.icon} aria-label="Edit relationship" onClick={() => (editing === e.id ? setEditing(null) : startEdit(e))}>✎</button>
              )}
              {onRemove && <button type="button" className={s.icon} aria-label="Remove" onClick={() => onRemove(e.id)}>×</button>}
            </div>
          </div>
          {editing === e.id && edgeFields && (
            <div className={s.edgeForm} data-testid={testid ? `${testid}-edge` : undefined}>
              <div className={s.edgeHead}>Relationship details</div>
              <Form fields={edgeFields} value={draft} onChange={(p) => setDraft((d) => ({ ...d, ...p }))}
                onSubmit={saveEdit} onCancel={() => setEditing(null)} submitLabel="Save" />
            </div>
          )}
        </div>
      ))}

      {past.length > 0 && (
        <button type="button" className={s.pastToggle} onClick={() => setShowPast((v) => !v)}>
          {showPast ? '▾ hide' : `▸ show ${past.length}`} past {past.length === 1 ? 'relationship' : 'relationships'}
        </button>
      )}

      {adding && addSearch && onAdd && (
        <Lookup presentation="popover" anchor={addRef.current} mode="multi" layout="cards"
          search={addSearch} types={addTypes} facets={addFacets} create={addCreate}
          testid={testid ? `${testid}-add` : undefined} commitLabel={(n) => `Add ${n}`}
          onClose={() => setAdding(false)}
          onCommit={(items) => { onAdd(items.map((i) => i.id)); setAdding(false) }} />
      )}
    </div>
  )
}
